// The signed link between a commit and the bytes that run (#1612).
//
// A released git tag had no verifiable link to any artifact. The two things we
// publish were in opposite states:
//
//   - The `dz-runner` binary is Ed25519-signed and boxes verify it before they
//     swap (`apps/runner/src/self-update.ts` `fetchAndVerify`). But the signature
//     covers the BINARY and nothing else — `manifest.json`, which is the only
//     place `commit` appears, is unsigned. So the chain proved "the fleet key
//     signed these bytes", never "these bytes were built from commit X".
//   - Container images had nothing at all: `provenance: false` + `sbom: false`
//     (deliberate, and staying — see `.github/workflows/release.yml` and
//     developerz-ai/infrastructure#738), no cosign, no attestation. ArgoCD Image
//     Updater pins a digest, which proves the bytes did not CHANGE, never that we
//     produced them.
//
// This document is the binding: one signed statement per commit naming every
// artifact that commit produced. It is ADDITIVE — no box reads it, and the payload
// boxes verify (the raw binary) is untouched, which is the one-way door #1612 names
// first: widening what the existing signature covers would change what a fleet
// already in the field can verify, and that cannot be renegotiated.
//
// ## Why the key is the COMMIT, not a version
//
// Three version namespaces exist and none of them spans both artifact planes:
// the runner publishes `<pkg>+sha-<short>`, images carry `:latest` + `:sha-<short>`,
// and the GitHub Release the maintainer bot cuts carries `v<semver>`
// (`packages/tools/src/idempotency.ts` — NOT `release.yml`, which contains no
// release step at all). The commit is the only identifier all three agree on, and
// it is the one a verifier can resolve a tag to. So the attestation is keyed on it.
//
// ## What is deliberately NOT in here
//
// **Image tags.** A tag is mutable; `:latest` moves on the next push. Recording one
// inside an immutable attestation would be a field that starts true and silently
// becomes false. The digest IS the identity — a tag adds nothing a verifier may rely
// on. Same reasoning the runner manifest applies to its `latest/` alias.

import { z } from 'zod';
import {
  type RunnerReleaseChannel,
  RunnerReleaseChannelSchema,
  type RunnerReleaseTarget,
  RunnerReleaseTargetSchema,
} from './fleet-update';

/**
 * Schema version of the published document. A LITERAL, not a range: a reader that
 * cannot name the shape it got must refuse rather than guess, because every field
 * here is load-bearing evidence. Bumping it is a new object shape, and the key
 * layout below is what lets both versions coexist.
 */
export const RELEASE_ATTESTATION_SCHEMA_VERSION = 1;

/** Full 40-hex git commit sha — the attestation's identity, and its path segment. */
const CommitShaSchema = z.string().regex(/^[0-9a-f]{40}$/, 'expected a 40-char lowercase git sha');

/** Lowercase hex sha-256, exactly 32 bytes. */
const Sha256HexSchema = z.string().regex(/^[0-9a-f]{64}$/, 'expected 64 lowercase hex chars');

/** An OCI content digest as the registry reports it (`sha256:<64 hex>`). */
const OciDigestSchema = z
  .string()
  .regex(/^sha256:[0-9a-f]{64}$/, 'expected an OCI digest of the form sha256:<64 hex>');

/** One container image this commit produced. */
export const ReleaseImageAttestationSchema = z.object({
  /** The app whose Dockerfile built it (`api`, `worker`, …) — the matrix leg's name. */
  app: z.string().min(1).max(64),
  /** Registry path without a tag, e.g. `registry.digitalocean.com/developerz-ai/developerz-api`. */
  repository: z.string().min(1).max(255),
  /** The immutable content digest the registry returned on push. */
  digest: OciDigestSchema,
});
export type ReleaseImageAttestation = z.infer<typeof ReleaseImageAttestationSchema>;

/** One runner binary this commit produced, with the url a verifier re-hashes. */
export const ReleaseRunnerArtifactAttestationSchema = z.object({
  target: RunnerReleaseTargetSchema,
  /** Lowercase hex sha-256 of the binary — re-computed by the verifier from `url`. */
  sha256: Sha256HexSchema,
  bytes: z.number().int().positive(),
  /** IMMUTABLE per-version url, never the channel alias — the alias moves under a halt. */
  url: z.string().url(),
  /** IMMUTABLE per-version url of the detached Ed25519 signature over the binary. */
  sig_url: z.string().url(),
});
export type ReleaseRunnerArtifactAttestation = z.infer<
  typeof ReleaseRunnerArtifactAttestationSchema
>;

/** The runner half of one commit's output — absent when this push built no runner. */
export const ReleaseRunnerAttestationSchema = z.object({
  version: z.string().min(1),
  channel: RunnerReleaseChannelSchema,
  artifacts: z.array(ReleaseRunnerArtifactAttestationSchema).min(1),
});
export type ReleaseRunnerAttestation = z.infer<typeof ReleaseRunnerAttestationSchema>;

/**
 * The signed document. Published as `releases/<commit>/release.json` with its
 * detached signature beside it at `.sig`.
 *
 * `runner` is nullable and `images` may be empty because `release.yml` builds each
 * half conditionally (`scripts/changed-apps.ts`): a docs-adjacent merge builds
 * neither, an `apps/api` change builds one image and no runner. An attestation that
 * names NOTHING is refused outright — a signed statement that a commit produced no
 * artifacts is not evidence, it is a document that would pass a verifier while
 * proving nothing, which is the failure mode this whole slice exists to remove.
 *
 * `pubkey` is a FINGERPRINT, never an input to verification — the same posture
 * `RunnerReleaseManifestSchema` takes. A verifier checks the signature against the
 * key IT holds (`DZ_UPDATE_PUBKEY`); a document naming a key we never issued must
 * fail closed, not be trusted about its own provenance.
 */
export const ReleaseAttestationSchema = z
  .object({
    schema: z.literal(RELEASE_ATTESTATION_SCHEMA_VERSION),
    commit: CommitShaSchema,
    built_at: z.string().datetime(),
    pubkey: Sha256HexSchema,
    runner: ReleaseRunnerAttestationSchema.nullable(),
    images: z.array(ReleaseImageAttestationSchema),
  })
  .refine((d) => d.runner !== null || d.images.length > 0, {
    message: 'a release attestation must name at least one artifact (runner or image)',
  });
export type ReleaseAttestation = z.infer<typeof ReleaseAttestationSchema>;

/** The prefix every published attestation lives under. */
export const RELEASE_ATTESTATION_PREFIX = 'releases/';

/**
 * Key of one commit's attestation: `releases/<commit>/release.json`.
 *
 * The commit is validated as 40-hex BEFORE it becomes a path segment, so — unlike
 * the runner's version segment, which has to fold arbitrary text
 * ({@link runnerReleaseVersionSegment}) — there is nothing here to sanitize away.
 * A caller that hands this a tag name or a short sha gets a throw, not a key.
 */
export function releaseAttestationKey(commit: string): string {
  return `${RELEASE_ATTESTATION_PREFIX}${CommitShaSchema.parse(commit)}/release.json`;
}

/** Key of the detached Ed25519 signature over the attestation bytes. */
export function releaseAttestationSigKey(commit: string): string {
  return `${releaseAttestationKey(commit)}.sig`;
}

/**
 * The exact bytes that get signed and published — canonical, so the producer and
 * every verifier agree byte-for-byte.
 *
 * `JSON.stringify` with two-space indent and a trailing newline, matching what
 * `scripts/runner-release.ts` publishes for `manifest.json`. Key ORDER is the
 * object-literal order the producer builds, which is why the producer builds it in
 * one place and hands the string around rather than re-serializing per consumer: a
 * signature over JSON is a signature over BYTES, and a re-serialization that reorders
 * keys verifies against nothing.
 */
export function serializeReleaseAttestation(attestation: ReleaseAttestation): string {
  return `${JSON.stringify(ReleaseAttestationSchema.parse(attestation), null, 2)}\n`;
}

/** Re-exported so a consumer of this document never has to reach into fleet-update too. */
export type { RunnerReleaseChannel, RunnerReleaseTarget };
